import React, { Fragment, useState, useEffect, useRef } from 'react';
import { injectIntl } from 'react-intl';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import GuidanceButton from '../GuidanceGroup/GuidanceButton/GuidanceButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import _ from 'lodash';
import Pikto from '../Pikto/Pikto';
import BottomSheetOrDialog from '../BottomSheetOrDialog/BottomSheetOrDialog';
import MyResizeObserver from '../MyResizeObserver/MyResizeObserver';
import { SlideTransition } from '../Transition/Transition';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { trackDirect } from '../../store/analytics/api';
import { tracking } from '../../utils/tracking';
import './ContentsDialog.scss';

export const getLocalePikto = (set, locale) => (set || {})[locale || 'zh-Hant-HK'];

const DialogStyle = {
  zIndex: 1301
};

// Tighter than the default 16px, so the close button and the title share one slim row.
const DialogTitleStyle = {
  paddingTop: 10,
  paddingBottom: 6
};

// On a phone an item opens as a bottom sheet over the library, which is a MUI Dialog (1301): the
// sheet's backdrop goes just above it, the sheet above that (ContentsDialog.scss).
const SHEET_BACKDROP_Z_INDEX = 1302;

// A chart's sheet is as tall as the chart, between half the screen and all of it, and can be pulled
// up to full height. Half at least, so a Piktochart growing in from its spinner does not jump the
// sheet from a sliver to the whole screen.
const CONTENT_SHEET = {
  snapPoints: ({ minHeight, maxHeight }) => [Math.min(Math.max(minHeight, maxHeight * 0.5), maxHeight), maxHeight],
  defaultSnap: ({ snapPoints }) => Math.min(...snapPoints)
};

// The full-screen tools (the recruitment calculators) are built for the whole screen.
const FULL_SCREEN_SHEET = { snapPoints: ({ maxHeight }) => [maxHeight] };

const ChartLoadingStyle = {
  display: 'flex',
  justifyContent: 'center',
  padding: '48px 0'
};

/** An item's title as its list entry shows it — a string, or a function of `intl`. */
const itemTitle = (item, intl) =>
  item && typeof item === 'object' ? (typeof item.title === 'function' ? item.title({ intl }) : item.title) : null;

// A zero-height sticky row, so the close button stays at the top of the dialog while it scrolls
// and still takes no room from the content below it.
const CloseRowStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  flex: 'none',
  height: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  pointerEvents: 'none'
};

// A light disc keeps the button readable if content scrolls up under it.
const CloseButtonStyle = {
  width: 40,
  height: 40,
  padding: 8,
  margin: 6,
  background: 'rgba(255, 255, 255, 0.92)',
  pointerEvents: 'auto'
};

const MainDialogContentStyle = {
  paddingBottom: 24
};

/**
 * The shortest label, from two characters up to six, that tells every chip in the rail apart:
 * two characters when the titles differ there ('香港', '政策'), longer when they share a start
 * ('MDRT Activity Calculator' and 'MDRT介紹' would both read 'MD').
 */
const MAX_CHIP_LABEL = 6;
export const chipLabels = titles => {
  const chars = titles.map(title => Array.from(title));
  const prefixOf = (own, length) => own.slice(0, length).join('').trim();
  return chars.map((own, index) => {
    for (let length = 2; length < Math.min(own.length, MAX_CHIP_LABEL); length += 1) {
      const prefix = prefixOf(own, length);
      if (!chars.some((other, otherIndex) => otherIndex !== index && prefixOf(other, length) === prefix)) return prefix;
    }
    return prefixOf(own, MAX_CHIP_LABEL);
  });
};

/**
 * The folded menu: one small chip per item, so a tool can still be picked with the
 * list out of the way. Each group keeps its heading (小工具, 香港理財行業), small. The
 * chip shows the first two characters of the title; the full title is the tooltip.
 *
 * A `special` group (an accordion in the full menu, headed by a logo) folds to its logo
 * and title, and opens its chips on tap — closed to begin with, like the accordion.
 */
export const MenuRail = props => {
  const { intl, sections, targetItem, onClickItem } = props;
  const [openGroups, setOpenGroups] = useState({});
  const headingOf = section => (typeof section.title === 'function' ? section.title({ intl }) : section.title) || '';
  const titleOf = item => (typeof item.title === 'function' ? item.title({ intl }) : item.title) || '';

  const allTitles = (sections || []).flatMap(section =>
    (section ? section.items || [] : []).filter(Boolean).map(titleOf)
  );
  const labels = chipLabels(allTitles);
  const labelOf = title => labels[allTitles.indexOf(title)] || title;

  const chipsOf = section =>
    (section.items || []).map(item => {
      if (!item) {
        return null;
      }
      const title = titleOf(item);
      const isSelected = targetItem?.id === item.id;
      return (
        <Tooltip key={item.id} title={title} placement="right">
          <Button
            className={`contents-menu-rail-chip${isSelected ? ' is-selected' : ''}`}
            aria-label={title}
            aria-current={isSelected ? 'true' : undefined}
            style={{ borderLeftColor: item.color }}
            onClick={() => onClickItem(item)}
          >
            {labelOf(title)}
          </Button>
        </Tooltip>
      );
    });

  return (
    <div className="contents-menu-rail" data-testid="contents-menu-rail">
      {(sections || []).map((section, index) => {
        if (!section) {
          return null;
        }
        const key = `${section.title}#${index}`;
        const heading = headingOf(section);

        if (section.special) {
          const isOpen = !!openGroups[key];
          return (
            <div className="contents-menu-rail-group" key={key}>
              <Button
                className="contents-menu-rail-special"
                aria-expanded={isOpen}
                aria-label={heading}
                onClick={() => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }))}
              >
                {section.img && React.isValidElement(section.img)
                  ? React.cloneElement(section.img, { width: 28 })
                  : null}
                <span>{heading}</span>
                <ExpandMoreIcon fontSize="small" color="primary" className={isOpen ? 'is-open' : undefined} />
              </Button>
              {isOpen && chipsOf(section)}
            </div>
          );
        }

        return (
          <div className="contents-menu-rail-group" key={key}>
            {heading && <div className="contents-menu-rail-heading">{heading}</div>}
            {chipsOf(section)}
          </div>
        );
      })}
    </div>
  );
};

const Chart = props => {
  const { targetItem, intl, ...rest } = props;

  // The half-second gap while a Piktochart is force-reloaded (`onClickItem`).
  if (targetItem === 'loading') {
    return (
      <div style={ChartLoadingStyle} data-testid="chart-loading">
        <CircularProgress size={32} />
      </div>
    );
  }

  if (targetItem) {
    if (targetItem.render) {
      if (typeof targetItem.render === 'function') {
        return targetItem.render({ intl, ...rest });
      } else {
        return targetItem.render;
      }
    } else if (targetItem.piktoChartIds) {
      const localeId = _.get(targetItem.piktoChartIds, `[${intl.locale}]`);
      return <Pikto uid={localeId} {...rest} />;
    }
  }

  return null;
};

const useSpecialSectionSummaryStyles = makeStyles()({
  content: {
    margin: '6px 0'
  }
});

const SpecialSection = props => {
  const { intl, img, title, items, onClickItem } = props;
  const { classes } = useSpecialSectionSummaryStyles();

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />} style={{ padding: '0 6px' }} classes={classes}>
        <Grid
          container
          spacing={1}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid>{img}</Grid>
          <Grid>
            <Typography>{title}</Typography>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails style={{ padding: '0 5px 0 3px' }}>
        <Section intl={intl} onClickItem={onClickItem} items={items} />
      </AccordionDetails>
    </Accordion>
  );
};

const Section = props => {
  const { title, ImgProps, items, onClickItem, targetItem, ...rest } = props;

  return (
    <Grid container spacing={1}>
      {title && (
        <Grid size={12}>
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Typography align="left">{typeof title === 'function' ? title(rest) : title}</Typography>
            </Grid>
            <Grid>
              <img width={24} {...ImgProps} />
            </Grid>
          </Grid>
        </Grid>
      )}
      {(items || []).map((item, index) => {
        if (!item) {
          return null;
        }

        const isSelected = targetItem?.id === item.id;

        return (
          <Grid key={index} size={12}>
            <GuidanceButton
              label={typeof item.title === 'function' ? item.title(rest) : item.title}
              labelProps={{
                align: 'left',
                style: { color: isSelected ? '#000' : undefined, fontWeight: isSelected ? 'bold' : 'normal' }
              }}
              color={item.color}
              redDot={item.redDot}
              onClick={() => onClickItem(item)}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

const ContentsDialog = props => {
  const {
    intl,
    history,
    title,
    sections,
    defaultItem,
    focusItem,
    open,
    onClose,
    onClosed,
    extras,
    className,
    type,
    style,
    collapsibleMenu,
    ...rest
  } = props;

  const isNarrowScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [targetItem, setTargetItem] = useState(!isNarrowScreen ? defaultItem : undefined);
  const [height, setHeight] = useState(undefined);
  // Opt-in (`collapsibleMenu`): fold the list away on a wide screen to give the pane the full width.
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const menuFolded = !!collapsibleMenu && !isNarrowScreen && menuCollapsed;

  /**
   * Where an open LANDS.
   *
   * Re-targeted on the OPENING edge, because "this open should show X" is a
   * statement about an open. It used to key off `defaultItem`'s identity
   * instead, which made a caller that wanted to land somewhere specific have to
   * hand back a different object each time — and made where the NEXT open
   * landed depend on which of two `onExited` hooks (this dialog's reset and the
   * narrow-screen chart dialog's) happened to write last.
   *
   * Resetting on close is therefore gone: nothing reads `targetItem` while the
   * dialog is shut (both the pane and the chart dialog below are gated on
   * `open`), so the value simply waits to be overwritten by the next open.
   * `onClosedChartDialog` still clears it, because that one means something
   * else — the adviser closed a chart to get back to the list.
   *
   * The edge, rather than `if (open)`, so that a caller who spells
   * `defaultItem` as an inline object cannot re-target the dialog on every
   * render and pin the adviser to it. No caller changes `defaultItem` while the
   * dialog is up — it covers the screen they would have to change it from.
   *
   * WHAT lands is two different questions, which is why there are two props:
   *
   *  - `focusItem` — a caller asking for one specific item ("the 活動量 tile
   *    promised the calculator"). An instruction, so it is honoured at every
   *    width.
   *  - `defaultItem` — what the DESKTOP side pane shows when nobody asked for
   *    anything. It fills a half of the dialog that is on screen either way, so
   *    there is no width at which leaving it blank is right.
   *
   * A phone has no side pane: the item opens as a second full-screen dialog
   * STACKED OVER the list. So an unasked-for default there is not a filled pane,
   * it is a chart the adviser has to dismiss to reach the library they tapped —
   * every single time, since this component is mounted for the whole session
   * (App/component.js) and keeps its state between opens. Hence `undefined`:
   * a plain open on a phone lands on the list, a focused one still lands on the
   * item it was sent for.
   */
  const landingItem = focusItem || defaultItem;
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setTargetItem(focusItem || (isNarrowScreen ? undefined : defaultItem));
    }
    wasOpen.current = open;
  }, [open, defaultItem, focusItem, isNarrowScreen]);

  useEffect(() => {
    // The item this open actually landed on, so a focused open is reported as
    // the thing that was asked for rather than as the library's front page.
    if (open && type === 'financial-charts') {
      tracking('Click on Financial Concept Item', { id: landingItem.id });
      trackDirect('viewFinancialChart', landingItem.id);
    } else if (open && type === 'recruitment') {
      tracking('Click on Recruitment Item', { id: landingItem.id });
      trackDirect('viewRecruitment', landingItem.id);
    }
  }, [open]);

  const formattedOnClosed = () => {
    if (onClosed) {
      onClosed();
    }
  };

  // Closing clears `targetItem` at once, but the phone's sheet is still sliding out: keep showing
  // the item it closed on until it is gone, rather than an empty sheet.
  const lastSheetItem = useRef(null);
  if (targetItem) lastSheetItem.current = targetItem;
  const sheetItem = targetItem || lastSheetItem.current;
  // A Piktochart is unmounted for half a second ('loading') to force a reload; the sheet keeps the
  // title of the item that is coming instead of opening blank.
  const reloadingItem = useRef(null);
  const sheetTitleItem = sheetItem === 'loading' ? reloadingItem.current : sheetItem;
  const closeChartDialog = () => setTargetItem(null);

  const onClosedChartDialog = () => setTargetItem(undefined);

  const onClickItem = item => {
    if (type === 'financial-charts') {
      tracking('Click on Financial Concept Item', { id: item.id });
      trackDirect('viewFinancialChart', item.id);
    } else if (type === 'recruitment') {
      tracking('Click on Recruitment Item', { id: item.id });
      trackDirect('viewRecruitment', item.id);
    }

    if (item && item.type === 'pikto') {
      // explicitly remove Pikto component to force-reload the content
      reloadingItem.current = item;
      setTargetItem('loading');
      setTimeout(() => {
        setTargetItem(item);
      }, 500);
    } else {
      setTargetItem(item);
    }
  };

  const onResize = entries => {
    if (!isNarrowScreen) {
      const target = _.get(entries, '[0].target');
      if (target) {
        if (target.offsetHeight) {
          setHeight(target.offsetHeight);
        }
      }
    }
  };

  return (
    <Fragment>
      {!isNarrowScreen && open && <MyResizeObserver targetsSelector=".contents-dialog .content" onResize={onResize} />}
      <Dialog
        className={`contents-dialog${className ? ` ${className}` : ''}`}
        open={open}
        onClose={onClose}
        fullWidth={true}
        maxWidth="lg"
        style={{ ...DialogStyle, ...style }}
        // A phone's item sheet lives outside this dialog; its focus trap would pull focus back
        // out of the sheet, and the calculators' inputs could not be typed into.
        disableEnforceFocus={isNarrowScreen && !!targetItem}
        {...rest}
        slots={{
          transition: SlideTransition
        }}
        slotProps={{
          transition: {
            onExited: formattedOnClosed
          }
        }}
      >
        <div style={CloseRowStyle}>
          <IconButton onClick={onClose} style={CloseButtonStyle} size="large">
            <CloseRoundedIcon />
          </IconButton>
        </div>
        <DialogTitle style={DialogTitleStyle}>{title}</DialogTitle>
        <DialogContent
          className="content"
          style={{
            ...MainDialogContentStyle,
            ...(!isNarrowScreen ? { height: height, overflow: 'hidden' } : undefined)
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'flex-start'
            }}
          >
            <Grid
              size={menuFolded ? { xs: 12, sm: 'auto' } : { xs: 12, sm: 3 }}
              style={
                !isNarrowScreen
                  ? {
                      height: height ? height - 24 : height,
                      overflow: 'auto'
                    }
                  : undefined
              }
            >
              {menuFolded && (
                <IconButton
                  aria-label={intl.formatMessage({ id: 'contents-menu-expand' })}
                  title={intl.formatMessage({ id: 'contents-menu-expand' })}
                  onClick={() => setMenuCollapsed(false)}
                  data-testid="contents-menu-expand"
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}
              {menuFolded && (
                <MenuRail intl={intl} sections={sections} targetItem={targetItem} onClickItem={onClickItem} />
              )}
              <Grid
                container
                spacing={2}
                sx={{ flexDirection: 'column', justifyContent: 'flex-start', display: menuFolded ? 'none' : undefined }}
              >
                {collapsibleMenu && !isNarrowScreen && (
                  <Grid sx={{ alignSelf: 'flex-start' }}>
                    <Button
                      size="small"
                      startIcon={<MenuOpenRoundedIcon />}
                      onClick={() => setMenuCollapsed(true)}
                      data-testid="contents-menu-collapse"
                    >
                      {intl.formatMessage({ id: 'contents-menu-collapse' })}
                    </Button>
                  </Grid>
                )}
                {sections.map((section, index) => {
                  if (!section) {
                    return null;
                  }

                  const key = `${section.title}#${index}`;

                  if (section.special) {
                    return (
                      <Grid key={key}>
                        <SpecialSection
                          intl={intl}
                          img={section.img}
                          title={section.title}
                          items={section.items}
                          onClickItem={onClickItem}
                          targetItem={targetItem}
                        />
                      </Grid>
                    );
                  } else {
                    return (
                      <Grid key={key}>
                        <Section
                          intl={intl}
                          onClickItem={onClickItem}
                          title={section.title}
                          items={section.items}
                          targetItem={targetItem}
                        />
                      </Grid>
                    );
                  }
                })}

                {extras && extras}
              </Grid>
            </Grid>
            {!isNarrowScreen && (
              <Grid
                size={menuFolded ? { sm: 'grow' } : { sm: 9 }}
                style={{ height: height ? height - 24 : height, overflow: 'auto' }}
              >
                {/* `|| defaultItem`, because at this width a blank half-dialog
                    is never the right answer and `targetItem` CAN be empty
                    here. Two ways: the narrow branch deliberately leaves it
                    unset on a plain open, and `closeChartDialog` clears it —
                    both of which a phone can carry into this branch by simply
                    turning sideways. `breakpoints.down('sm')` is 600px, so a
                    375×812 handset crosses it on rotation, and the pane that
                    appeared was empty. */}
                <Chart targetItem={open ? targetItem || defaultItem : undefined} intl={intl} history={history} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
      {isNarrowScreen && (
        <BottomSheetOrDialog
          className="contents-chart-sheet"
          open={open && !!targetItem}
          onClose={closeChartDialog}
          onExited={onClosedChartDialog}
          header={itemTitle(sheetTitleItem, intl)}
          content={<Chart targetItem={sheetItem} intl={intl} history={history} />}
          BottomSheetProps={sheetTitleItem && sheetTitleItem.fullScreenDialog ? FULL_SCREEN_SHEET : CONTENT_SHEET}
          BottomSheetParams={{ zIndex: SHEET_BACKDROP_Z_INDEX }}
        />
      )}
    </Fragment>
  );
};

export default injectIntl(ContentsDialog);
